import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API_URLS, API_CONFIG } from '../../config/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  ArrowLeft,
  Star,
  Clock,
  Users,
  CheckCircle,
  Zap,
  Globe,
  Palette,
  Smartphone,
  Server,
  Megaphone,
  TrendingUp,
  Award,
  Shield,
  MessageSquare,
  Calendar,
  DollarSign,
  Eye,
  Download,
  ExternalLink,
  Play,
  Code,
  Database,
  Layers,
  Target,
  Rocket,
  Heart,
  ThumbsUp,
  Share2,
  Bookmark,
  Briefcase,
  FileText,
  Settings,
  Lightbulb,
  BarChart3,
  Mail,
  Phone,
  MapPin,
  Timer,
  CheckSquare,
  ArrowRight,
  Plus,
  Minus,
  Info,
  AlertCircle,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import ConsultationForm from '../ConsultationForm';
import MediaDisplay from '../MediaDisplay';

const SubcategoryDetailPage = () => {
  const { categoryId, subcategoryId } = useParams();
  const [subcategory, setSubcategory] = useState(null);
  const [category, setCategory] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFeatures, setExpandedFeatures] = useState(false);
  const [expandedProcess, setExpandedProcess] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [contentReady, setContentReady] = useState(false);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Icon mapping
  const iconMap = {
    Briefcase, Globe, Palette, Smartphone, Zap, Server, 
    Megaphone, Code, Database, Layers, Target, Rocket,
    Heart, Star, Award, Shield, Users, TrendingUp
  };

  useEffect(() => {
    fetchData();
  }, [categoryId, subcategoryId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (!subcategoryId) {
        setError('Alt kategori ID bulunamadı');
        return;
      }
      
      // Convert subcategoryId back to original name
      // moda-magazasi -> Moda Mağazası
      const subcategoryName = subcategoryId
        .replace(/-/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
        .replace('Magazasi', 'Mağazası')
        .replace('Elektronik Magaza', 'Elektronik Mağaza')
        .replace('Gida Ve Icecek', 'Gıda & İçecek')
        .replace('Kozmetik Magaza', 'Kozmetik Mağaza');
      
      // Fetch subcategory details from API
      const response = await fetch(`${API_URLS.SUBCATEGORY_DETAILS}&category_id=${categoryId}&subcategory_name=${encodeURIComponent(subcategoryName)}`);
      
      const responseText = await response.text();
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Response was not JSON:', responseText);
        setError('Sunucudan geçersiz yanıt alındı');
        return;
      }
      
      if (data.status === 'error') {
        setError(data.message || 'Alt kategori bulunamadı');
        return;
      }
      
      setCategory(data.category);
      setSubcategory(data.subcategory);
      setProjects(data.projects || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  if (error || !subcategory || !category) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {error || 'Alt Kategori Bulunamadı'}
          </h1>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Ana sayfaya dön
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = iconMap[category.icon] || Briefcase;

  // Features from category data
  const features = category?.features || [
    'Modern ve Responsive Tasarım',
    'SEO Optimizasyonu',
    'Hızlı Yükleme Süreleri',
    'Güvenli Ödeme Sistemleri',
    'Mobil Uyumlu Arayüz',
    'Analitik ve Raporlama',
    '7/24 Teknik Destek',
    'Ücretsiz Bakım ve Güncelleme'
  ];

  // Process steps from subcategory or default
  const process = subcategory?.process_steps || [
    {
      step: 1,
      title: 'Analiz ve Planlama',
      description: 'Projenizin gereksinimlerini analiz ediyor ve detaylı bir plan oluşturuyoruz.',
      duration: '1-2 gün',
      icon: 'Target'
    },
    {
      step: 2,
      title: 'Tasarım ve Prototip',
      description: 'Kullanıcı deneyimi odaklı tasarım ve interaktif prototipler hazırlıyoruz.',
      duration: '3-5 gün',
      icon: 'Palette'
    },
    {
      step: 3,
      title: 'Geliştirme',
      description: 'Modern teknolojiler kullanarak projenizi geliştiriyoruz.',
      duration: '2-4 hafta',
      icon: 'Code'
    },
    {
      step: 4,
      title: 'Test ve Optimizasyon',
      description: 'Kapsamlı testler yapıyor ve performans optimizasyonu gerçekleştiriyoruz.',
      duration: '3-5 gün',
      icon: 'CheckCircle'
    },
    {
      step: 5,
      title: 'Yayın ve Destek',
      description: 'Projenizi canlıya alıyor ve sürekli destek sağlıyoruz.',
      duration: 'Sürekli',
      icon: 'Rocket'
    }
  ];

  const getProcessIcon = (iconName) => {
    const processIconMap = {
      Target, Palette, Code, CheckCircle, Rocket, Users, Clock, Settings
    };
    return processIconMap[iconName] || Target;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section 
        className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white py-20 relative overflow-hidden"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-6">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Ana Sayfa
            </Link>
            <ChevronRight className="w-4 h-4 text-white/60" />
            <Link 
              to={`/kategori/${category?.slug || categoryId}`} 
              className="text-white/80 hover:text-white transition-colors"
            >
              {category.name}
            </Link>
            <ChevronRight className="w-4 h-4 text-white/60" />
            <span className="text-white font-medium">{subcategory.name}</span>
          </div>
          
          <div className="text-center space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-3 bg-white/20 backdrop-blur-lg px-6 py-3 rounded-full text-lg font-medium">
                <IconComponent className="w-6 h-6" />
                <span>{subcategory.name}</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold leading-tight">
                {subcategory.name}
              </h1>
              
              <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
                {category.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div 
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        {/* Özellikler */}
        <div className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Sol Taraf - Kartlar */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Neden {subcategory.name}?
                </h2>
                <p className="text-gray-600">
                  {subcategory.name} alanında uzman ekibimizle birlikte başarılı projeler gerçekleştirin.
                </p>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                {features.slice(0, 6).map((feature, index) => {
                  // Handle both old string format and new object format
                  const featureName = typeof feature === 'string' ? feature : feature.name;
                  const featureDescription = typeof feature === 'string' 
                    ? `Profesyonel ${feature.toLowerCase()} özelliği ile projenizi en üst seviyeye taşıyoruz.`
                    : feature.description;
                  
                  return (
                    <div key={index} className="bg-white p-4 rounded-xl shadow-sm border">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 bg-gradient-to-r ${category.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{featureName}</h3>
                          <p className="text-gray-600 text-sm">
                            {featureDescription}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sağ Taraf - Resim/Video */}
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl overflow-hidden">
                <MediaDisplay
                  src={subcategory?.image_url || "https://placehold.co/600x600/e0e7ff/6366f1?text=Proje+Görseli"}
                  alt={subcategory.name}
                  className="w-full h-full object-cover"
                  videoProps={{ controls: true }}
                />
              </div>
            </div>
          </div>
          
          {features.length > 6 && (
            <div className="text-center">
              <button
                onClick={() => setExpandedFeatures(!expandedFeatures)}
                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2 mx-auto"
              >
                {expandedFeatures ? (
                  <>
                    <Minus className="w-4 h-4" />
                    Daha Az Göster
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Tüm Özellikleri Gör ({features.length - 6} daha)
                  </>
                )}
              </button>
              
              {expandedFeatures && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                  {features.slice(6).map((feature, index) => {
                    // Handle both old string format and new object format
                    const featureName = typeof feature === 'string' ? feature : feature.name;
                    const featureDescription = typeof feature === 'string' 
                      ? `Profesyonel ${feature.toLowerCase()} özelliği ile projenizi en üst seviyeye taşıyoruz.`
                      : feature.description;
                    
                    return (
                      <div key={index + 6} className="bg-white p-6 rounded-xl shadow-sm border">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-8 h-8 bg-gradient-to-r ${category.color} rounded-lg flex items-center justify-center`}>
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                          <h3 className="font-semibold text-gray-900">{featureName}</h3>
                        </div>
                        <p className="text-gray-600 text-sm">
                          {featureDescription}
                        </p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Süreç */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Proje Süreci
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {subcategory.name} projenizi adım adım nasıl gerçekleştirdiğimizi keşfedin.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {process.map((step, index) => {
              const ProcessIcon = getProcessIcon(step.icon);
              return (
                <div key={index} className="relative h-full flex flex-col">
                  <div className="bg-white p-6 rounded-xl shadow-sm border flex-1 flex flex-col">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-10 h-10 bg-gradient-to-r ${category.color} rounded-full flex items-center justify-center text-white font-bold`}>
                        {step.step}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{step.title}</h3>
                        <p className="text-sm text-gray-500">{step.duration}</p>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm flex-1">{step.description}</p>
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gray-300 transform -translate-y-1/2"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Örnek Projeler */}
        {projects.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Tamamlanan Projeler
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {subcategory.name} alanında gerçekleştirdiğimiz başarılı projelerden örnekler.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <Card key={project.id} className="overflow-hidden">
                  <div className="aspect-video bg-gray-100 relative">
                    {project.image_url ? (
                      <MediaDisplay
                        src={project.image_url}
                        alt={project.name}
                        className="w-full h-full object-cover"
                        videoProps={{ controls: true }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <div className="w-12 h-12 mx-auto mb-2 bg-gray-200 rounded"></div>
                          <p>Görsel Yok</p>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-green-500 text-white">
                        {(() => {
                          const statusMap = {
                            'completed': 'Tamamlandı',
                            'in_progress': 'Devam Ediyor',
                            'planned': 'Planlanıyor'
                          };
                          return statusMap[project.status] || project.status;
                        })()}
                      </Badge>
                    </div>
                    <div className="absolute bottom-3 left-3">
                      <Badge variant="secondary" className="bg-white/90">
                        {project.project_year}
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {project.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3">
                      {project.client_name}
                    </p>
                    <p className="text-gray-600 text-sm mb-4">
                      {project.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.features.slice(0, 3).map((feature, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {feature}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="text-sm font-medium">{project.rating}</span>
                        <span className="text-sm text-gray-500">({project.reviews_count})</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {project.duration}
                      </div>
                    </div>
                    
                    <div className="flex justify-end items-center gap-2">
                      {project.project_url && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => window.open(project.project_url, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Siteyi Gör
                        </Button>
                      )}
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/project/${project.id}`}>
                          <Eye className="w-4 h-4 mr-1" />
                          Detay
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">
            {subcategory.name} Projenizi Başlatalım!
          </h2>
          <p className="text-purple-100 mb-8 max-w-2xl mx-auto">
            Uzman ekibimizle birlikte {subcategory.name.toLowerCase()} alanında hayalinizdeki projeyi gerçekleştirelim. Size özel çözümler üretiyor, projelerinizi başarıyla tamamlıyoruz.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setShowConsultationForm(true)} className="bg-white text-purple-600 hover:bg-gray-100">
              <MessageSquare className="w-5 h-5 mr-2" />
              Ücretli Danışmanlık
            </Button>
            <Button onClick={() => { window.location.href = '/iletisim'; }} variant="outline" className="border-white text-white hover:bg-white hover:text-purple-600">
              <Calendar className="w-5 h-5 mr-2" />
              İletişim
            </Button>
          </div>
        </div>
      </div>

      {/* Consultation Form Modal */}
      {showConsultationForm && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConsultationForm(false);
            }
          }}
        >
          <div className="relative z-50 max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
            <ConsultationForm isModal={true} onClose={() => setShowConsultationForm(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubcategoryDetailPage;